﻿import React, {useEffect, useMemo, useRef, useState} from 'react';
import {Canvas} from 'fabric';
import AssetSelectAndUploadModal, {
    AssetSelectAndUploadModalOptions
} from '../assets/AssetSelectAndUploadModal.tsx';
import {FileFormat} from '../../lib/FileFormats.ts';
import useProjectData, {
    ReactionEvent
} from '../../project_data/useProjectData.ts';
import ReconnectionModal from '../../project_data/ReconnectionModal.tsx';
import {ObjectTypes, CustomCanvas} from './types.ts';
import {XIcon} from 'lucide-react';
import Icon from '../shared/Icon.tsx';
import {useAuthStateManager} from '../../hooks/useAuthStateManager.tsx';
import MainToolbar from './MainToolbar.tsx';
import TextToolbar from './TextToolbar.tsx';
import StickyNoteToolbar from './StickyNoteToolbar.tsx';
import ResourceCardToolbar from './ResourceCardToolbar.tsx';
import ImageToolbar from './ImageToolbar.tsx';
import ReactionToolbar from './ReactionToolbar.tsx';
import EditToolbar from './EditToolbar.tsx';
import VideoToolbar from './VideoToolbar.tsx';
import CanvasController from './CanvasController.ts';
import createObservableClass from '../../lib/createObservableClass.ts';
import useClientConfig from '../../hooks/useClientConfig.tsx';
import {
    FileDataModel,
    MixModel,
    Selection,
    ProjectDataIndex
} from '../../api/types.ts';
import {useEffectOnce} from '../../hooks/useEffectOnce.ts';
import {useNavigate} from 'react-router-dom';
import PencilToolbar from './PencilToolbar.tsx';
import EmojiPickerModal from '../../emojis/EmojiPickerModal.tsx';
import {Emoji} from '../../emojis/types.ts';
import emojisByEmojiCode from '../../emojis/emojisByEmojiCode.ts';
import {handleReaction} from './reactions.ts';
import {useQuery} from 'react-query';
import {toast} from 'react-toastify';
import useMixById from '../../api/mixes/useMixById.ts';
import tryParseGuid from '../../lib/tryParseGuid.ts';
import useLocalState from '../../lib/useLocalState.ts';
import FocusModeExitControl from '../shared/FocusModeExitControl.tsx';
import ChooseProjectTabModal from './ChooseProjectTabModal.tsx';
import {isRenderMode, useTabView} from '../../lib/tabView.ts';

interface CanvasComponentProps {
    project: MixModel;
    dataIndex: ProjectDataIndex;
    readOnly: boolean;
    reactionsEnabled: boolean;
    isFocusMode?: boolean;
    onExitFocusMode?: () => void;
}

// Keys pressed in a form field or a text being edited belong to it, not to the board.
const isTypingTarget = (target: EventTarget | null) => {
    const element = target as HTMLElement | null;
    return (
        element instanceof HTMLInputElement ||
        element instanceof HTMLTextAreaElement ||
        element instanceof HTMLSelectElement ||
        element?.isContentEditable === true
    );
};

/*
 * The board's keyboard shortcuts (the toolbar tooltips name them): Delete,
 * Ctrl+Z undo and Ctrl+Shift+Z or Ctrl+Y redo, Ctrl+A select all, Ctrl+D
 * duplicate, Ctrl+G group and Ctrl+Shift+G ungroup, Ctrl+] and Ctrl+[ one
 * step forward or back (with Shift, all the way), the arrow keys nudge by 1
 * (Shift: 10, or a grid step when snapping), Escape clears the selection.
 * Ctrl stands in for Cmd on a Mac.
 */
const whiteboardShortcut = (
    event: KeyboardEvent
): ((controller: CanvasController) => void) | null => {
    const modifier = event.ctrlKey || event.metaKey;
    const key = event.key.toLowerCase();

    if (key === 'delete' || key === 'backspace') {
        return controller => controller.onDelete();
    }
    if (key === 'escape') {
        return controller => controller.deselect();
    }
    if (key.startsWith('arrow') && !modifier) {
        const step = event.shiftKey ? 10 : 1;
        const dx = key === 'arrowleft' ? -step : key === 'arrowright' ? step : 0;
        const dy = key === 'arrowup' ? -step : key === 'arrowdown' ? step : 0;
        return controller => controller.nudgeSelected(dx, dy);
    }
    if (!modifier) {
        return null;
    }

    switch (key) {
        case 'z':
            return event.shiftKey
                ? controller => controller.redo()
                : controller => controller.undo();
        case 'y':
            return controller => controller.redo();
        case 'a':
            return controller => controller.selectAll();
        case 'd':
            return controller => controller.duplicateObject();
        case 'g':
            return event.shiftKey
                ? controller => controller.ungroupItems()
                : controller => controller.groupItems();
        case ']':
            return event.shiftKey
                ? controller => controller.bringToFront()
                : controller => controller.bringForward();
        case '[':
            return event.shiftKey
                ? controller => controller.sendToBack()
                : controller => controller.bringBackwards();
        default:
            return null;
    }
};

const CanvasComponent: React.FC<CanvasComponentProps> = ({
    project,
    dataIndex,
    readOnly,
    reactionsEnabled,
    isFocusMode = false,
    onExitFocusMode = () => undefined
}) => {
    const navigate = useNavigate();
    const {id} = project;
    const projectId = id!;

    const canvasRef = useRef<HTMLCanvasElement | null>(null);

    const emojiContainerRef = useRef<HTMLDivElement | null>(null);

    const {authData} = useAuthStateManager();
    const {storageUri} = useClientConfig();

    // First-run hint pointing editors at the toolbar (dismissed per browser).
    const [hintDismissed, setHintDismissed] = useLocalState(
        'whiteboardHintDismissed',
        false
    );
    const showCanvasHint = !readOnly && !hintDismissed;
    const dismissCanvasHint = () => setHintDismissed(true);

    const playerTimersRef = useRef<{
        [playerId: string]: ReturnType<typeof setTimeout>;
    }>({});

    const openAssetSelectAndUploadModal = (
        fileFormat: FileFormat,
        assetExplanation: string,
        onFileSelected: (file: FileDataModel | null) => void
    ) => {
        const onFileSelectionChanged = (files: FileDataModel[] | undefined) => {
            if (!files) {
                return;
            }

            if (files.length > 0) {
                onFileSelected(files[0]);
            } else {
                onFileSelected(null);
            }

            closeAssetSelectAndUploadModal();
        };

        setAssetSelectAndUploadModalOptions({
            selection: Selection.Single,
            explanation: assetExplanation,
            formats: fileFormat,
            fileSelectionChanged: onFileSelectionChanged
        });
    };

    const [renderToken, setRenderToken] = useState(0);
    const [showReactions, setShowReactions] = useState(false);
    const [selectedEmoji, setSelectedEmoji] = useState(
        emojisByEmojiCode['grinning_face']
    );

    const stateHasChanged = () =>
        setRenderToken(prevDummy => (prevDummy === 1000 ? 0 : prevDummy + 1));

    /*
     * Upgrading a legacy project card needs the live project, which the old
     * card never stored. The lookup stays here in React rather than in the
     * controller, so the canvas keeps knowing nothing about the network.
     */
    const [legacyCardRef, setLegacyCardRef] = useState<string | null>(null);
    const legacyCardMixId = legacyCardRef ? tryParseGuid(legacyCardRef) : null;
    const fetchLegacyCardMix = useMixById({
        mixId: legacyCardMixId,
        customUrl: legacyCardRef && !legacyCardMixId ? legacyCardRef : null
    });

    const controller = useMemo(
        () =>
            createObservableClass(
                new CanvasController(
                    readOnly,
                    authData,
                    playerTimersRef,
                    storageUri,
                    openAssetSelectAndUploadModal,
                    () => stateHasChanged(),
                    navigate,
                    `${projectId}_${dataIndex}`
                ),
                () => {
                    // Force a re-render by updating the dummy state
                    stateHasChanged();
                },
                [
                    'videoElements',
                    'activeObjectType',
                    'currentColor',
                    'fontSettings',
                    'players',
                    'movingCanvas',
                    'canvasTransform',
                    'isDrawing',
                    'isSelectActive',
                    'isReadOnly'
                ]
            ),
        []
    );

    useQuery(['legacy-project-card', legacyCardRef], fetchLegacyCardMix, {
        enabled: !!legacyCardRef,
        cacheTime: 0,
        onSuccess: (mix: MixModel) => {
            setLegacyCardRef(null);

            if (mix) {
                controller.replaceLegacyProjectCard(mix);
            } else {
                toast.error('That project could not be found.');
            }
        },
        onError: () => {
            setLegacyCardRef(null);
            toast.error('Unable to refresh this card.');
        }
    });

    // The card being pointed at a tab, by its project id.
    const [cardTabProjectId, setCardTabProjectId] = useState<string | null>(
        null
    );

    const onChooseProjectCardTab = () => {
        const card = controller.activeProjectCard;
        if (card?.mixId) {
            setCardTabProjectId(card.mixId);
        }
    };

    const onRefreshProjectCard = () => {
        const ref = controller.selectedLegacyProjectCardRef;

        if (ref) {
            setLegacyCardRef(ref);
        }
    };

    useEffect(() => {
        controller.setIsReadOnly(readOnly);
    }, [readOnly]);

    const {
        projectData,
        manipulation,
        playerEvent,
        reactionEvent,
        isLoading,
        isError,
        showReconnectionModal,
        closeReconnectionModal,
        reinitialize
    } = useProjectData({
        projectId,
        dataIndex,
        onRemoteManipulation: {
            mergeData: (elementId, data, meta) =>
                controller.onRemoteMergeData(elementId, data, meta),
            removeElement: (elementId, meta) =>
                controller.onRemoteRemoveElement(elementId, meta)
        },
        onInitialDataFetched: initialData =>
            controller.onInitialDataFetched(initialData),
        onRemotePlayerEvent: playerEvent =>
            controller.onRemotePlayerEvent(playerEvent),
        onRemoteReactionEvent: reactionEvent =>
            onRemoteReactionEvent(reactionEvent)
    });

    const projectDataRef = useRef(projectData);

    useEffect(() => {
        projectDataRef.current = projectData;
    }, [projectData]);

    useEffect(() => {
        if (!controller.initialized && canvasRef.current) {
            const canvas = new Canvas(canvasRef.current) as CustomCanvas;

            controller.initialize(
                canvas,
                () => projectDataRef.current,
                manipulation,
                playerEvent
            );
        }
    }, [canvasRef.current, controller.initialized]);

    // Links can open the whiteboard at a spot, and the render page moves the view (lib/tabView.ts).
    useTabView(dataIndex, controller.viewController);

    useEffectOnce(() => {
        return () => {
            controller.dispose();
        };
    });

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (isTypingTarget(event.target) || controller.isEditingText) {
                return;
            }

            const shortcut = whiteboardShortcut(event);
            if (!shortcut) {
                return;
            }

            event.preventDefault();
            shortcut(controller);
        };

        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, []);

    const [
        assetSelectAndUploadModalOptions,
        setAssetSelectAndUploadModalOptions
    ] = useState<AssetSelectAndUploadModalOptions | null>(null);

    const [isReaction, setIsReaction] = useState<boolean>(false);

    const closeAssetSelectAndUploadModal = () => {
        setAssetSelectAndUploadModalOptions(null);
    };

    const controlsDisabled = isLoading || controller.isReadOnly;

    if (isError) {
        return <>Error loading project data</>;
    }

    const enableReaction = () => {
        if (isReaction) {
            setIsReaction(false);
        } else {
            setIsReaction(true);
        }
    };

    const onRemoteReactionEvent = (reactionEvent: ReactionEvent) => {
        if (!canvasRef.current || !emojiContainerRef.current) {
            return;
        }

        const emojiContainer = emojiContainerRef.current;

        handleReaction(reactionEvent, emojiContainer);
    };

    const triggerReactionEvent = ({
        animationType,
        message,
        emoji
    }: {
        animationType: 'float' | 'drop' | 'fly';
        message: string | null;
        emoji: string | null;
    }) => {
        if (!canvasRef.current || !emojiContainerRef.current) {
            return;
        }

        const emojiContainer = emojiContainerRef.current;
        const canvasWidth = emojiContainer.offsetWidth;
        const canvasHeight = emojiContainer.offsetHeight;

        switch (animationType) {
            case 'drop':
                {
                    const reaction: ReactionEvent = {
                        animationType,
                        message,
                        emoji,
                        color: message == null ? null : controller.currentColor,
                        top: null,
                        left: Math.random() * (canvasWidth - 30)
                    };

                    handleReaction(reaction, emojiContainer);

                    reactionEvent(reaction);
                }

                break;
            case 'float':
                {
                    const reaction: ReactionEvent = {
                        animationType,
                        message,
                        emoji,
                        color: message == null ? null : controller.currentColor,
                        top: null,
                        left: Math.random() * canvasWidth
                    };

                    handleReaction(reaction, emojiContainer);

                    reactionEvent(reaction);
                }

                break;
            case 'fly':
                {
                    const reaction: ReactionEvent = {
                        animationType,
                        message,
                        emoji,
                        color: message == null ? null : controller.currentColor,
                        top: Math.random() * (canvasHeight - 100),
                        left: null
                    };

                    handleReaction(reaction, emojiContainer);

                    reactionEvent(reaction);
                }

                break;
        }
    };

    const onReactAsync = async (emoji: Emoji) => {
        setSelectedEmoji(emoji);
        setShowReactions(false);
    };

    return (
        <>
            {showReconnectionModal && !readOnly && (
                <ReconnectionModal
                    reinitialize={reinitialize}
                    onClose={closeReconnectionModal}
                />
            )}

            {assetSelectAndUploadModalOptions && (
                <AssetSelectAndUploadModal
                    options={assetSelectAndUploadModalOptions}
                    onClose={closeAssetSelectAndUploadModal}
                />
            )}

            {cardTabProjectId && (
                <ChooseProjectTabModal
                    projectRef={cardTabProjectId}
                    initialTarget={controller.activeProjectCard?.target}
                    confirmLabel="Save"
                    onClose={() => setCardTabProjectId(null)}
                    onChosen={(_mix, target) => {
                        controller.setProjectCardTarget(target);
                        setCardTabProjectId(null);
                    }}
                />
            )}
            <div
                className={`canvas-focus-surface focus-mode-surface${
                    isFocusMode ? ' focus-mode-surface--active' : ''
                }`}
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}
            >
                {isFocusMode && (
                    <FocusModeExitControl onExit={onExitFocusMode} />
                )}
                <div
                    id="CanvasContainer"
                    style={{
                        position: 'relative',
                        width: '100%',
                        height: '100%'
                    }}
                >
                    {showReactions && (
                        <EmojiPickerModal
                            onClose={() => setShowReactions(false)}
                            onSelect={onReactAsync}
                        />
                    )}

                    <div>
                        <canvas
                            ref={canvasRef}
                            style={{
                                width: '100%',
                                height: '100%'
                            }}
                        />

                        <div
                            ref={emojiContainerRef}
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '93%',
                                pointerEvents: 'none'
                            }}
                        />
                    </div>
                    {!readOnly && showCanvasHint && (
                        <div className="mx-canvas-hint" role="status">
                            <span>
                                Use the toolbar below to add drawings, text,
                                images and more.
                            </span>
                            <button
                                type="button"
                                onClick={dismissCanvasHint}
                                aria-label="Dismiss"
                            >
                                <Icon icon={XIcon} size="xs" strokeWidth={2.5} />
                            </button>
                        </div>
                    )}
                    {/* The render page takes pictures of the whiteboard alone. */}
                    {!isRenderMode() && (
                        <div id="mainToolbar" className="mx-canvas-toolbar-bottom">
                            <MainToolbar
                                isReaction={isReaction}
                                controlsDisabled={controlsDisabled}
                                setIsReaction={enableReaction}
                                controller={controller}
                                readOnly={readOnly}
                                renderToken={renderToken}
                                reactionsEnabled={reactionsEnabled}
                                mixId={projectId}
                            />
                        </div>
                    )}
                    {controller.showPencilToolbar && (
                        <div id="penciltoolbar" className="mx-canvas-toolbar-above">
                            <PencilToolbar controller={controller} />
                        </div>
                    )}

                    {isReaction && (
                        <div id="reactionToolbar" className="mx-canvas-toolbar-above">
                            <ReactionToolbar
                                openReactionModal={() => setShowReactions(true)}
                                triggerReactionEvent={triggerReactionEvent}
                                emoji={selectedEmoji}
                            />
                        </div>
                    )}

                    {controller.activeObjectType !== null &&
                        controller.activeObjectType !== ObjectTypes.Text &&
                        controller.activeObjectType !==
                            ObjectTypes.StickyNote &&
                        controller.activeObjectType !==
                            ObjectTypes.ResourceCard &&
                        controller.activeObjectType !== ObjectTypes.Image &&
                        !controller.isReadOnly &&
                        !controller.movingCanvas &&
                        !controller.isDrawing && (
                            <div id="editToolbar" className="mx-canvas-toolbar-top">
                                <EditToolbar
                                    onRefreshProjectCard={onRefreshProjectCard}
                                    onChooseProjectCardTab={
                                        onChooseProjectCardTab
                                    }
                                    onRemoveSelected={controller.removeSelected}
                                    onDuplicate={controller.duplicateObject}
                                    onBringForward={controller.bringForward}
                                    onBringBackwards={controller.bringBackwards}
                                    onToFront={controller.bringToFront}
                                    onSendToBack={controller.sendToBack}
                                    onGroupItems={controller.groupItems}
                                    onUngroupItems={controller.ungroupItems}
                                    onAlign={controller.alignSelectedItems}
                                    onDistribute={
                                        controller.distributeSelectedItems
                                    }
                                    controlsDisabled={controlsDisabled}
                                    activeSelection={
                                        controller.activeObjectType
                                    }
                                    activeObjectMeta={
                                        controller.activeObjectMeta
                                    }
                                />
                            </div>
                        )}

                    {controller.activeObjectType == ObjectTypes.ResourceCard &&
                        !controller.isReadOnly &&
                        !controller.movingCanvas &&
                        !controller.isDrawing && (
                            <div id="resourceCardToolbars" className="mx-canvas-toolbar-top">
                                <EditToolbar
                                    onRemoveSelected={controller.removeSelected}
                                    onDuplicate={controller.duplicateObject}
                                    onBringForward={controller.bringForward}
                                    onBringBackwards={controller.bringBackwards}
                                    onToFront={controller.bringToFront}
                                    onSendToBack={controller.sendToBack}
                                    onGroupItems={controller.groupItems}
                                    onUngroupItems={controller.ungroupItems}
                                    onAlign={controller.alignSelectedItems}
                                    onDistribute={
                                        controller.distributeSelectedItems
                                    }
                                    controlsDisabled={controlsDisabled}
                                    activeSelection={
                                        controller.activeObjectType
                                    }
                                    activeObjectMeta={
                                        controller.activeObjectMeta
                                    }
                                />
                                <div id="ResourceCardToolbar">
                                    <ResourceCardToolbar
                                        controlsDisabled={controlsDisabled}
                                        controller={controller}
                                    />
                                </div>
                            </div>
                        )}

                    {controller.activeObjectType == ObjectTypes.Image &&
                        !controller.isReadOnly &&
                        !controller.movingCanvas &&
                        !controller.isDrawing && (
                            <div id="imageToolbars" className="mx-canvas-toolbar-top">
                                <EditToolbar
                                    onRemoveSelected={controller.removeSelected}
                                    onDuplicate={controller.duplicateObject}
                                    onBringForward={controller.bringForward}
                                    onBringBackwards={controller.bringBackwards}
                                    onToFront={controller.bringToFront}
                                    onSendToBack={controller.sendToBack}
                                    onGroupItems={controller.groupItems}
                                    onUngroupItems={controller.ungroupItems}
                                    onAlign={controller.alignSelectedItems}
                                    onDistribute={
                                        controller.distributeSelectedItems
                                    }
                                    controlsDisabled={controlsDisabled}
                                    activeSelection={
                                        controller.activeObjectType
                                    }
                                    activeObjectMeta={
                                        controller.activeObjectMeta
                                    }
                                />
                                <div id="ImageToolbar">
                                    <ImageToolbar
                                        controlsDisabled={controlsDisabled}
                                        controller={controller}
                                    />
                                </div>
                            </div>
                        )}

                    {controller.activeObjectType == ObjectTypes.StickyNote &&
                        !controller.isReadOnly &&
                        !controller.movingCanvas &&
                        !controller.isDrawing && (
                            <div id="stickyNoteToolbars" className="mx-canvas-toolbar-top">
                                <EditToolbar
                                    onRemoveSelected={controller.removeSelected}
                                    onDuplicate={controller.duplicateObject}
                                    onBringForward={controller.bringForward}
                                    onBringBackwards={controller.bringBackwards}
                                    onToFront={controller.bringToFront}
                                    onSendToBack={controller.sendToBack}
                                    onGroupItems={controller.groupItems}
                                    onUngroupItems={controller.ungroupItems}
                                    onAlign={controller.alignSelectedItems}
                                    onDistribute={
                                        controller.distributeSelectedItems
                                    }
                                    controlsDisabled={controlsDisabled}
                                    activeSelection={
                                        controller.activeObjectType
                                    }
                                    activeObjectMeta={
                                        controller.activeObjectMeta
                                    }
                                />
                                <div id="StickyNoteToolbar">
                                    <StickyNoteToolbar
                                        controlsDisabled={controlsDisabled}
                                        controller={controller}
                                    />
                                </div>
                            </div>
                        )}

                    {controller.activeObjectType == ObjectTypes.Text &&
                        !controller.isReadOnly &&
                        !controller.movingCanvas &&
                        !controller.isDrawing && (
                            <div id="textToolbars" className="mx-canvas-toolbar-top">
                                <div id="editToolbar">
                                    <EditToolbar
                                        onRemoveSelected={
                                            controller.removeSelected
                                        }
                                        onDuplicate={controller.duplicateObject}
                                        onBringForward={controller.bringForward}
                                        onBringBackwards={
                                            controller.bringBackwards
                                        }
                                        onToFront={controller.bringToFront}
                                        onSendToBack={controller.sendToBack}
                                        onGroupItems={controller.groupItems}
                                        onUngroupItems={controller.ungroupItems}
                                        onAlign={controller.alignSelectedItems}
                                        onDistribute={
                                            controller.distributeSelectedItems
                                        }
                                        controlsDisabled={controlsDisabled}
                                        activeSelection={
                                            controller.activeObjectType
                                        }
                                        activeObjectMeta={
                                            controller.activeObjectMeta
                                        }
                                    />
                                </div>
                                <div id="TextToolbar">
                                    <TextToolbar
                                        controlsDisabled={controlsDisabled}
                                        controller={controller}
                                    />
                                </div>
                            </div>
                        )}

                    {controller.activeObjectType == ObjectTypes.Video &&
                        !controller.isReadOnly &&
                        !controller.movingCanvas && (
                            <>
                                <div id="videoToolbar" className="mx-canvas-toolbar-top">
                                    <VideoToolbar
                                        controlsDisabled={controlsDisabled}
                                        pauseSelectedVideo={
                                            controller.pauseSelectedVideo
                                        }
                                        playSelectedVideo={
                                            controller.playSelectedVideo
                                        }
                                    />
                                </div>
                            </>
                        )}
                </div>

                {/*
                {controller.getSelections().map((selection, index) => (
                    <div
                        key={index}
                        style={{border: '1px solid purple'}}
                        className="mt-1 p-2"
                    >
                        SELECTION{' '}
                        <pre>{JSON.stringify(selection, null, 2)}</pre>
                    </div>
                ))}

                <br />

                {controller
                    .getObjects()
                    .filter(obj => obj.type != 'sprite')
                    .map((obj, index) => {
                        const object = obj as CustomFabricObject;
                        return (
                            <div
                                key={index}
                                style={{border: '1px solid #DDD'}}
                                className="mt-1 p-2"
                            >
                                <pre>
                                    {JSON.stringify(
                                        {
                                            ...obj.toObject(),
                                            elementId: object.elementId,
                                            ordinalPosition:
                                                object.ordinalPosition,
                                            meta: object.meta,
                                            nav: object.nav
                                        },
                                        null,
                                        2
                                    )}
                                </pre>
                            </div>
                        );
                    })}
                */}
            </div>
        </>
    );
};

export default CanvasComponent;
